/* eslint max-lines: off */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useAsync } from 'react-use';
import { useHistory } from 'react-router';
import styled from 'styled-components/macro';

import { OpenInfoFromWorkflow, Group, GroupForInfo, HistoryRecord } from 'models/response';

import selectors from 'state/selectors';
import service from 'services';
import utils from 'utils';
import actions from 'state/actions';

import TextArea from 'components/Input/Textarea';
import Table from 'components/Table';
import Modal from 'components/Modal';

import Hierarchy from 'components/Hierarchy';

const TableContainer = styled.div`
  overflow: auto;
`;

const Section = styled.div`
  margin: 30px 0;
`;

export const Title = styled.h4`
  margin-bottom: 10px;
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

export const Left = styled.div`
  width: 220px;
`;

export const Right = styled.div`
  width: 100%;
  max-height: 350px;
  overflow: auto;
  margin-left: 10px;
  padding-right: 10px;
`;

export const CardContainer = styled.div`
  max-height: 350px;
  overflow: auto;
  padding-right: 15px;
`;

const InfoFromWf = () => {
  const t = utils.intl.useTranslator();
  const dispatch = useDispatch();
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const documents = useSelector(selectors.documents.selectAllActiveDocuments);
  const idCompany = documents[0]?.idCompany || -1;
  const idUser = useSelector(selectors.user.getIdUser) || -1;
  const [infoFromWorkflowResponse, setInfoFromWorkflowResponse] = useState<OpenInfoFromWorkflow | null>(null);
  const [rowsHistory, setRowsHistory] = useState<HistoryRecord[]>([]);
  const [note, setNote] = useState<string>('');
  const history = useHistory();

  const userPreference = useSelector(selectors.user.getUserPreference);
  const [selectedDefinition, setSelectedDefinition] = useState<GroupForInfo | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);

  useAsync(async () => {
    try {
      if (protocol) {
        const { data } = await service.openInfoFromWorkflow({ company: idCompany, protocol });
        setRowsHistory(data.wfMovements);
        setInfoFromWorkflowResponse(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const historyColumns = [
    {
      accessor: 'causalCode',
      Header: t('causalCode'),
    },
    { accessor: 'note', Header: t('note') },
    { accessor: 'protocol', Header: t('protocol') },
    {
      accessor: 'rejectionReason',
      Header: t('rejectionReason'),
      Cell: ({ value }: { value: null | string }) => value ?? '',
    },
    {
      accessor: 'sendDate',
      Header: t('sendDate'),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, userPreference.timeZone),
    },
    {
      accessor: 'type',
      Header: t('type'),
    },
    {
      accessor: 'typeDescription',
      Header: t('typeDescription'),
    },
  ];

  const columns = [
    { accessor: 'username', Header: 'User' },
    { accessor: 'name', Header: 'Name' },
  ];

  const searchTypeValues: { value: string; label: string; parent: 'one' | 'two' | 'three' | 'table' }[] = [
    { value: 'task', label: 'Task', parent: 'one' },
    { value: 'group', label: 'Group', parent: 'two' },
    { value: 'user', label: 'User', parent: 'table' },
  ];

  const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  };

  const handleOpenInfo = async () => {
    try {
      if (protocol && infoFromWorkflowResponse && selectedDefinition) {
        await service.openWfInfo({
          protocols: [protocol],
          idUser,
          idGroup: selectedGroups[0].idGroup,
          idWorkflowDefinition: selectedDefinition.idWorkflowDefinition,
          idCompany,
          note,
        });
        dispatch(actions.modal.closeModal());
        dispatch(actions.documents.removeRows([protocol]));
        if (utils.documents.isOpenDocumentView()) dispatch(actions.documents.openNextDocument(history));
        utils.app.notify('success', t('success_info_wf'));
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Modal.Header title={t('info-from-wf-title')} subtitle={t('info-from-wf-desc')} />
      <Modal.Content>
        {rowsHistory.length ? (
          <>
            <Title>{t('storico')}</Title>
            <TableContainer>
              <Table id="info-from-wf-table" hasPagination hasSort columns={historyColumns} rows={rowsHistory} />
            </TableContainer>
          </>
        ) : null}
        {infoFromWorkflowResponse ? (
          <Section>
            <Hierarchy
              title="destinatario"
              hasSearch
              searchTypeValues={searchTypeValues}
              parentsOne={infoFromWorkflowResponse.getGroupsForInfo}
              parentOneName="wfName"
              parentOneId="idWorkflowDefinition"
              parentOneSelection={(value: any) => {
                setSelectedDefinition(value);
                setSelectedGroups([]);
              }}
              parentOneChild="groups"
              parentTwoName="groupName"
              parentTwoId="idGroup"
              parentTwoSelection={(isActive?: boolean, value?: any) => {
                setSelectedGroups(value);
              }}
              parentTwoChild="users"
              parentTwoHasTable={true}
              idRowTable="idUser"
              columns={columns}
              tableValueName="username"
              tableValueNameTwo="name"
            />
          </Section>
        ) : null}
        <Section>
          <Title>{t('note')}</Title>
          <TextArea value={note} name="Nota" placeholder={t('note here')} onChange={onNoteChange} />
        </Section>
      </Modal.Content>
      <Modal.Footer
        confirmText={t('continue')}
        confirmAction={handleOpenInfo}
        confirmDisabled={!selectedDefinition || !selectedGroups.length}
      />
    </>
  );
};

export default InfoFromWf;
